import { useState } from "react";
import { FadeUp } from "./Reveal";
import { useLang } from "./LanguageContext";

const copy = {
  en: {
    kicker: "The Hands Behind the Show",
    h2: <>The maestri, <em className="italic">face to face.</em></>,
    intro: "No stage names, no theatrics off the pot — just two artisans who have spent a lifetime with warm dough and curd between their hands. These are the people your guests will meet.",
    introLux: "No stage names, no theatrics off the pot — just two artisans who have spent a lifetime with warm curd and fresh milk between their hands. These are the people your guests will meet.",
    members: [
      {
        img: "/stage/carlo-editorial.webp",
        pos: "object-[50%_18%]",
        role: "Carlo — The Maestro Casaro",
        tag: "A Casaro for Three Generations",
        bio: "A casaro for three generations — the craft handed down from father to son. Carlo leads the ritual: he reads the whey, feels the curd, and calls the exact moment the stretch begins.",
      },
      {
        img: "/stage/raffaele-editorial.webp",
        pos: "object-[50%_30%]",
        role: "Raffaele — Fire, Pinsa & Burrata",
        tag: "The Giant Pan & The Pairing",
        bio: "Raffaele commands the giant pan at grand events, bakes live from the wood-fired oven, and signs the burrata pairings — figs, heirloom tomatoes, cold-pressed oil, composed in front of the table.",
      },
    ],
    wl: {
      role: "Raffaele — Fire, Pizza & Burrata",
      tag: "The Giant Pan & The Gourmet Plate",
      bio: "Raffaele commands the giant pan at grand events, bakes long-fermented pizza live and brings gourmet plates to life with a still-warm burrata.",
      plateTitle: "The Gourmet Plate",
      plateTag: "Sea, Land & Freshness",
      plateDesc: "Shrimp tartare with avocado in a toasted-almond crust, still-warm burrata, physalis and confit tomatoes.",
    },
  },
  it: {
    kicker: "Le Mani Dietro lo Show",
    h2: <>I maestri, <em className="italic">a viso aperto.</em></>,
    intro: "Nessun nome d'arte, nessuna teatralità fuori dal pentolone — solo due artigiani che hanno passato una vita con impasto e cagliata calda tra le mani. Sono loro che i tuoi ospiti incontreranno.",
    introLux: "Nessun nome d'arte, nessuna teatralità fuori dal pentolone — solo due artigiani che hanno passato una vita con cagliata calda e latte fresco tra le mani. Sono loro che i tuoi ospiti incontreranno.",
    members: [
      {
        img: "/stage/carlo-editorial.webp",
        pos: "object-[50%_18%]",
        role: "Carlo — Il Maestro Casaro",
        tag: "Casaro da Tre Generazioni",
        bio: "Casaro da tre generazioni — il mestiere tramandato di padre in figlio. Carlo guida il rituale: legge il siero, sente la cagliata e decide l'attimo esatto in cui inizia la filatura.",
      },
      {
        img: "/stage/raffaele-editorial.webp",
        pos: "object-[50%_30%]",
        role: "Raffaele — Fuoco, Pinsa & Burrata",
        tag: "Il Padellone & L'Abbinamento",
        bio: "Raffaele comanda il padellone nei grandi eventi, inforna dal vivo e firma il condimento delle burrate — fichi, pomodori antichi, olio a crudo, composti davanti al tavolo.",
      },
    ],
    wl: {
      role: "Raffaele — Fuoco, Pizza & Burrata",
      tag: "Il Padellone & Il Piatto Gourmet",
      bio: "Raffaele comanda il padellone nei grandi eventi, inforna dal vivo la pizza a lunga fermentazione e dà vita a piatti gourmet con una burrata ancora tiepida.",
      plateTitle: "Il Piatto Gourmet",
      plateTag: "Mare, Terra e Freschezza",
      plateDesc: "Tartare di gambero e avocado in crosta di mandorle tostate, burrata ancora tiepida, physalis e pomodorini confit.",
    },
  },
  ru: {
    kicker: "Руки за кулисами шоу",
    h2: <>Мастера, <em className="italic">лицом к лицу.</em></>,
    intro: "Никаких сценических имён — только два ремесленника, всю жизнь работающие с тёплым тестом и сгустком. Именно их встретят ваши гости.",
    introLux: "Никаких сценических имён — только два ремесленника, всю жизнь работающие с тёплым сгустком и свежим молоком. Именно их встретят ваши гости.",
    members: [
      {
        img: "/stage/carlo-editorial.webp",
        pos: "object-[50%_18%]",
        role: "Карло — Маэстро Казаро",
        tag: "Казаро в трёх поколениях",
        bio: "Сыровар в трёх поколениях — ремесло, переданное от отца к сыну. Карло ведёт ритуал: читает сыворотку, чувствует сгусток и выбирает точный момент начала вытягивания.",
      },
      {
        img: "/stage/raffaele-editorial.webp",
        pos: "object-[50%_30%]",
        role: "Раффаэле — Огонь и Буррата",
        tag: "Гигантская сковорода и подача",
        bio: "Раффаэле командует гигантской сковородой на больших событиях, печёт вживую и подписывает подачу буррат — инжир, старинные томаты, масло первого отжима, прямо у стола.",
      },
    ],
    wl: {
      role: "Раффаэле — Огонь, Пицца и Буррата",
      tag: "Гигантская сковорода и гурмэ-тарелка",
      bio: "Раффаэле командует гигантской сковородой на больших событиях, печёт вживую пиццу длительной ферментации и создаёт гурмэ-тарелки с ещё тёплой бурратой.",
      plateTitle: "Гурмэ-тарелка",
      plateTag: "Море, земля и свежесть",
      plateDesc: "Тартар из креветки с авокадо в миндальной корочке, ещё тёплая буррата, физалис и томаты конфи.",
    },
  },
  es: {
    kicker: "Las Manos Detrás del Show",
    h2: <>Los maestros, <em className="italic">cara a cara.</em></>,
    intro: "Sin nombres artísticos, sin teatralidad fuera de la olla — solo dos artesanos que han pasado una vida con masa tibia y cuajada entre las manos. Son ellos a quienes tus invitados conocerán.",
    introLux: "Sin nombres artísticos, sin teatralidad fuera de la olla — solo dos artesanos que han pasado una vida con cuajada tibia y leche fresca entre las manos. Son ellos a quienes tus invitados conocerán.",
    members: [
      {
        img: "/stage/carlo-editorial.webp",
        pos: "object-[50%_18%]",
        role: "Carlo — El Maestro Casaro",
        tag: "Casaro de Tres Generaciones",
        bio: "Casaro de tres generaciones — el oficio transmitido de padre a hijo. Carlo guía el ritual: lee el suero, siente la cuajada y decide el instante exacto en que empieza el hilado.",
      },
      {
        img: "/stage/raffaele-editorial.webp",
        pos: "object-[50%_30%]",
        role: "Raffaele — Fuego, Pinsa & Burrata",
        tag: "La Sartén Gigante & El Maridaje",
        bio: "Raffaele comanda la sartén gigante en los grandes eventos, hornea en vivo y firma el aliño de las burratas — higos, tomates antiguos, aceite en crudo, compuestos frente a la mesa.",
      },
    ],
    wl: {
      role: "Raffaele — Fuego, Pizza & Burrata",
      tag: "La Sartén Gigante & El Plato Gourmet",
      bio: "Raffaele comanda la sartén gigante en los grandes eventos, hornea en vivo pizza de larga fermentación y da vida a platos gourmet con una burrata aún tibia.",
      plateTitle: "El Plato Gourmet",
      plateTag: "Mar, Tierra y Frescura",
      plateDesc: "Tartar de camarón y aguacate en costra de almendras tostadas, burrata aún tibia, physalis y tomates confitados.",
    },
  },
};

const luxOverride = {
  en: { role: "Raffaele — Fire & Burrata", bio: "Raffaele commands the giant pan at grand events and brings gourmet plates to life with a still-warm burrata." },
  it: { role: "Raffaele — Fuoco & Burrata", bio: "Raffaele comanda il padellone nei grandi eventi e dà vita a piatti gourmet con una burrata ancora tiepida." },
  ru: { role: "Раффаэле — Огонь и Буррата", bio: "Раффаэле командует гигантской сковородой на больших событиях и создаёт гурмэ-тарелки с ещё тёплой бурратой." },
  es: { role: "Raffaele — Fuego & Burrata", bio: "Raffaele comanda la sartén gigante en los grandes eventos y da vida a platos gourmet con una burrata aún tibia." },
};

const Plate3D = ({ src, alt }) => {
  const [rot, setRot] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(false);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const py = ((e.clientY - r.top) / r.height - 0.5) * 2;
    setRot({ x: py * -14, y: px * 22 });
  };
  return (
    <div
      className="relative aspect-[3/4] rounded-sm shadow-[0_30px_60px_-24px_rgba(0,0,0,0.7)] select-none"
      style={{ perspective: "900px", touchAction: "pan-y" }}
      onPointerDown={(e) => { if (e.pointerType !== "mouse") return; setDrag(true); e.currentTarget.setPointerCapture(e.pointerId); move(e); }}
      onPointerUp={() => { setDrag(false); setRot({ x: 0, y: 0 }); }}
      onPointerLeave={() => { setDrag(false); setRot({ x: 0, y: 0 }); }}
      onPointerMove={(e) => { if (e.pointerType !== "mouse") return; move(e); }}
      data-testid="plate-3d"
    >
      <div
        className="absolute inset-0 overflow-hidden rounded-sm"
        style={{ transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`, transition: drag ? "none" : "transform 0.6s cubic-bezier(0.22,1,0.36,1)", transformStyle: "preserve-3d" }}
      >
        <img src={src} alt={alt} className="w-full h-full object-cover pointer-events-none" draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
      </div>
      <span className="hidden md:block absolute top-3 right-3 font-sans text-[10px] uppercase tracking-[0.2em] text-bg/70 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1.5 pointer-events-none">
        3D · Drag
      </span>
    </div>
  );
};

export const Team = ({ whiteLabel = false, luxury = false }) => {
  const { lang } = useLang();
  const t = copy[lang];
  const ov = luxury ? { ...t.wl, ...luxOverride[lang] } : whiteLabel ? t.wl : null;
  const members = ov ? [t.members[0], { ...t.members[1], ...ov }] : t.members;
  const showPlate = whiteLabel || luxury;
  return (
    <section id="team" className="py-24 md:py-40 bg-gradient-to-b from-[#120a0c] to-wine text-bg overflow-hidden" data-testid="team-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 md:mb-20 max-w-2xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">{t.h2}</h2>
          <p className="font-sans text-base md:text-lg text-bg/70 mt-8 leading-relaxed">{luxury ? t.introLux : t.intro}</p>
        </FadeUp>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 ${showPlate ? "lg:grid-cols-3 max-w-6xl" : "max-w-4xl"}`}>
          {members.map((m, i) => (
            <FadeUp key={m.role} delay={i * 0.1}>
              <figure className="group" data-testid={`team-member-${i + 1}`}>
                <div className="relative overflow-hidden rounded-sm aspect-[3/4] shadow-[0_30px_60px_-24px_rgba(0,0,0,0.7)]">
                  <img
                    src={m.img}
                    alt={m.role}
                    className={`w-full h-full object-cover ${m.pos} transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                <figcaption className="mt-6">
                  <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-terracotta mb-2">{m.tag}</p>
                  <h3 className="font-serif text-2xl md:text-3xl">{m.role}</h3>
                  <p className="font-sans text-sm md:text-base text-bg/70 leading-relaxed mt-3">{m.bio}</p>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
          {showPlate && (
            <FadeUp delay={0.2}>
              <figure className="group" data-testid="team-gourmet-plate">
                <Plate3D src="/stage/gourmet-plate.webp" alt={t.wl.plateTitle} />
                <figcaption className="mt-6">
                  <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-terracotta mb-2">{t.wl.plateTag}</p>
                  <h3 className="font-serif text-2xl md:text-3xl">{t.wl.plateTitle}</h3>
                  <p className="font-sans text-sm md:text-base text-bg/70 leading-relaxed mt-3">{t.wl.plateDesc}</p>
                </figcaption>
              </figure>
            </FadeUp>
          )}
        </div>
      </div>
    </section>
  );
};
